import PageWrapper from "@/components/PageWrapper";
import { ErrorBoundary } from "react-error-boundary";
import { ErrorHandler } from "@/components/error-hanlder/ErrorHandler";
import { logError } from "@/components/error-hanlder/error-handler.lib";
import { Suspense, useRef, useState, FormEvent, useEffect } from "react";
import {
  useSuspenseQuery,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  useLessonListOptions,
  useLessonDetailOptions,
  useVerifyKeywordMutation,
  useStartTestMutation,
  TestingKeys,
} from "./testing.api";
import { LessonItem } from "./testing.types";
import { useNavigate, useSearchParams } from "react-router";
import { useTranslation, Trans } from "react-i18next";
import BaseButton from "@/components/base/button/BaseButton";
import { formatNumber } from "@/shared/utils";
import { Sheet } from "react-modal-sheet";
import IconClose from "@/assets/icons/close.svg?react";
import IconPlay from "@/assets/icons/play.svg?react";
import IconExternalLink from "@/assets/icons/external-link.svg?react";
import { Info } from "lucide-react";
import VibratingButton from "@/components/base/button/VibratingButton";
import { Input } from "@/components/ui/input";
import { pasteClipboardContent } from "@/shared/utils/common";
import TaskBaseButton from "@/components/base/button/TaskBaseButton";
import TaskButton from "@/components/base/button/TaskButton";
import type { Status } from "@/components/base/button/TaskButton";
import ChevronRight from "@/assets/icons/chevron-right.svg?react";
import IconChevron from "@/assets/icons/chevron.svg?react";
import IconTestList from "@/assets/icons/test-list-icon.svg?react"
import IconLock from "@/assets/icons/lock.svg?react";
import DonDialog from "@/components/base/DonDialog";
import { useDialog } from "@/shared/hooks/useDialog";
import showCustomToast from "@/shared/utils/showCustomToast";
import NoData from "@/components/common/NoData";

const LessonListPage = () => {
  return (
    <ErrorBoundary FallbackComponent={ErrorHandler} onError={logError}>
      <Suspense fallback={<LessonListLoading />}>
        <BaseLessonListPage />
      </Suspense>
    </ErrorBoundary>
  );
};

const LessonListLoading = () => (
  <PageWrapper>
    <div className="container pt-2 pb-6">
      <div className="skeleton w-36 h-6 rounded-md mb-3" />
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="relative mb-3 bg-white/08 rounded-10 border-b border-white/08">
          <div className="bg-white/08 border-b flex items-center justify-center border-white/08 text-xs font-semibold leading-4 -top-2 -left-2 absolute p-1 rounded-[6px] z-10">
            <span className="skeleton inline-block w-3 h-3 rounded" />
          </div>
          {/* Video row */}
          <div className="py-3 px-2 flex items-center">
            <div className="flex items-center gap-2.5 flex-1 min-w-0">
              <div className="skeleton rounded-full shrink-0 w-9 h-9" />
              <div className="flex-1 min-w-0">
                <div className="skeleton w-3/4 h-[14px] rounded-md mb-1" />
                <div className="skeleton w-16 h-3 rounded-md" />
              </div>
            </div>
            <div className="skeleton w-[90px] h-[28px] rounded-full shrink-0" />
          </div>
          {/* Divider */}
          <div className="h-px bg-white/08 mx-2" />
          {/* Test row */}
          <div className="py-3 px-2 flex items-center">
            <div className="flex gap-2 items-center flex-1 min-w-0">
              <div className="skeleton rounded-full shrink-0 w-9 h-9" />
              <div className="flex-1 min-w-0">
                <div className="skeleton w-10 h-[14px] rounded-md mb-1" />
                <div className="skeleton w-14 h-3 rounded-md" />
              </div>
            </div>
            <div className="skeleton w-[90px] h-[28px] rounded-full shrink-0" />
          </div>
        </div>
      ))}
    </div>
  </PageWrapper>
);

const BaseLessonListPage = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const collectionId = searchParams.get("collection");
  const { data: lessons } = useSuspenseQuery(useLessonListOptions(collectionId));
  const navigate = useNavigate();

  return (
    <PageWrapper backPath={collectionId ? `/get-don/${collectionId}` : "/get-don"}>
      <div className="container pt-2 pb-6">
        <h2 className="text-lg font-bold leading-130 text-white mb-3">{t("video_lessons")}:</h2>
        {lessons.length === 0 ? (
          <div className="flex-1 flex-center min-h-[60vh]">
            <NoData
              image={<img src="images/common-no-data.png" className="size-24" />}
              title={t("no_data_title")}
              description={t("no_data_subtitle")}
              headerClassName="bg-none p-0 m-0"
            />
          </div>
        ) : (
          <div className="flex flex-col">
            {lessons.map((lesson, index) => (
              <LessonRows
                key={lesson.id}
                lesson={lesson}
                index={index}
                navigate={navigate}
                collectionId={collectionId}
              />
            ))}
          </div>
        )}
      </div>
    </PageWrapper>
  );
};

// ─── Lesson Rows (Video + Test) ───

interface LessonRowsProps {
  lesson: LessonItem;
  index: number;
  navigate: ReturnType<typeof useNavigate>;
  collectionId: string | null;
}

const LessonRows = ({ lesson, index, navigate, collectionId }: LessonRowsProps) => {
  const { t } = useTranslation();
  const isLocked = lesson.is_locked;
  // Watched across detail-page round-trips: LessonDetailPage writes a
  // sessionStorage flag on mount, list page reads it. React Router unmounts
  // this page on navigation away, so the read runs fresh on return.
  const videoWatched = (() => {
    try {
      return sessionStorage.getItem(`lesson_watched_${lesson.id}`) === "1";
    } catch {
      return false;
    }
  })();
  const [keywordSheetOpen, setKeywordSheetOpen] = useState(false);
  const [keywordVerified, setKeywordVerified] = useState(false);

  // Keyword sheet state
  const [inputValue, setInputValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();
  const { mutateAsync: verifyKeyword, isPending: isVerifying } = useVerifyKeywordMutation();

  // Get lesson detail for tests list. Plain useQuery (not suspense) so each
  // row loads independently: with up to 100 lessons, a shared suspense
  // boundary made the whole list wait for every detail request and threw the
  // entire list into the ErrorBoundary if a single one failed.
  const { data: lessonDetail } = useQuery(useLessonDetailOptions(lesson.id));
  const tests = lessonDetail?.tests || [];

  // keyword_completed: backend yoki local verify natijasi
  const keywordCompleted = lesson.keyword_completed || keywordVerified;
  const testsLocked = isLocked || (lesson.has_keyword && !keywordCompleted);

  // Derive video button status from backend fields + client state
  const videoStatus: Status = (() => {
    if (keywordCompleted) return "COMPLETED";
    if (videoWatched) return "PERFORMED";
    return "INCOMPLETE";
  })();

  const handleVideoButtonClick = () => {
    if (videoStatus === "INCOMPLETE") {
      // Open the dedicated lesson detail page (video on top + script
      // below) instead of the legacy fullscreen-only modal.
      const qs = collectionId ? `?collection=${collectionId}` : "";
      navigate(`/testing/lesson/${lesson.id}${qs}`);
    } else if (videoStatus === "PERFORMED") {
      setKeywordSheetOpen(true);
      setInputValue("");
    }
  };

  // Keyword submit
  useEffect(() => {
    if (keywordSheetOpen) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [keywordSheetOpen]);

  const handleKeywordSubmit = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!inputValue.trim()) return;

    try {
      const res = await verifyKeyword({
        lessonId: lesson.id,
        keyword: inputValue.trim(),
      });

      if (res.is_correct) {
        setKeywordSheetOpen(false);
        setKeywordVerified(true);
        // No DON here — keyword is just the gate that unlocks the test.
        // The single lesson DON fires after the user finishes the test
        // (auto-claimed by submit_test -> claim_lesson_award on the backend).
        showCustomToast(t("keyword_correct") || "Kalit so'z to'g'ri", "success");
        queryClient.invalidateQueries({ queryKey: [TestingKeys.LessonList] });
      } else {
        showCustomToast(t("keyword_incorrect"), "error");
      }
    } catch {
      showCustomToast(t("global_error"), "error");
      queryClient.invalidateQueries({ queryKey: [TestingKeys.LessonList] });
      queryClient.invalidateQueries({ queryKey: ["profile"] });
    }
  };

  const handlePaste = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const content = await pasteClipboardContent();
    setInputValue(content);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  return (
    <>
      {/* Combined lesson card: video + test */}
      <div className="relative mb-3 bg-white/08 rounded-10 border-b border-white/08">
        <div className="bg-white/08 backdrop-blur-[18px] text-white border-b border-white/08 text-xs font-semibold leading-4 -top-2 -left-2 absolute py-0.5 rounded-[6px] px-2 z-10">{index + 1}</div>

        {/* Video row */}
        <div className="py-3 px-2 flex items-center">
          <button
            type="button"
            disabled={isLocked}
            onClick={() => {
              const qs = collectionId ? `?collection=${collectionId}` : "";
              navigate(`/testing/lesson/${lesson.id}${qs}`);
            }}
            className="flex items-center gap-2.5 flex-1 min-w-0 text-left active:opacity-70 transition-opacity disabled:opacity-100 disabled:active:opacity-100"
          >
            <div className="bg-white/08 rounded-full p-2 flex-center shrink-0">
              <IconPlay className="size-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm mb-1 font-semibold text-white leading-130 truncate">{lesson.name}</p>
              <p className="text-xs text-gray-1 leading-130 truncate">
                {t("video_and_keyword") || "Video + kalit so'z"}
              </p>
            </div>
          </button>
          {isLocked ? (
            <TaskButton status="COMPLETED" disabled suffix={<IconLock className="size-4" />} className="!text-gray-1" customText={t("watch")} />
          ) : (
            <TaskButton
              status={videoStatus}
              onClick={handleVideoButtonClick}
              customText={videoStatus === "INCOMPLETE" ? t("watch") : undefined}
            />
          )}
        </div>

        {/* Test row */}
        {tests.length > 0 && (
          <>
            <div className="h-px bg-white/08 mx-2" />
            <div className="py-3 px-2 flex items-center">
              <div className="flex gap-2 items-center flex-1 min-w-0">
                <div className="bg-white/08 rounded-full p-2 flex-center shrink-0">
                  <IconTestList />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm mb-1 font-semibold text-white leading-130 truncate">{t("test")}</p>
                  <p className="text-xs text-[#FBC76C] leading-130">
                    {formatNumber(Number(lesson.max_test_don) || 0)} {t("DON")}
                  </p>
                </div>
              </div>
              {lesson.test_passed ? (
                <TaskButton status="COMPLETED" />
              ) : !testsLocked ? (
                <TestStartButtonDirect
                  testId={(tests.find(te => !te.user_passed) || tests[0]).id}
                  lessonId={lesson.id}
                  collectionId={collectionId}
                  navigate={navigate}
                  onWatchVideo={() => {
                    const qs = collectionId ? `?collection=${collectionId}` : "";
                    navigate(`/testing/lesson/${lesson.id}${qs}`);
                  }}
                  maxDon={Number(lesson.max_test_don) || 0}
                  totalQuestions={tests.reduce((s, te) => s + te.questions_count, 0)}
                />
              ) : (
                <TaskButton status="COMPLETED" disabled suffix={<IconLock className="w-4 h-4" />} className="text-xs font-semibold !text-gray-1 flex items-center justify-center gap-0.5" customText={t("test")} />
              )}
            </div>
          </>
        )}
      </div>

      {/* Keyword Bottom Sheet */}
      <Sheet
        rootId="root"
        detent="content-height"
        isOpen={keywordSheetOpen}
        onClose={() => setKeywordSheetOpen(false)}
        onMouseDown={(e) => e.stopPropagation()}
        tweenConfig={{ ease: [0.16, 1, 0.3, 1], duration: 0.55 }}
      >
        <Sheet.Container className="!bg-gray-4 rounded-t-2xl">
          <Sheet.Header className="[&_.react-modal-sheet-header]:!py-2 [&_.react-modal-sheet-header]:!h-5 !bg-gray-4 rounded-t-2xl" />
          <Sheet.Content className="bg-gray-4 px-4">
            <form className="pb-safe-bottom" onSubmit={handleKeywordSubmit}>
              <div className="flex items-center justify-end gap-3 pb-3">
                <VibratingButton type="button" onClick={() => setKeywordSheetOpen(false)}>
                  <IconClose className="text-gray-1 size-6 cursor-pointer" />
                </VibratingButton>
              </div>

              {/* Lesson info + watch video button */}
              <div className="p-3 border border-white/08 rounded-10 bg-white/08 mb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="bg-white/08 rounded-full w-8 h-8 flex-center shrink-0">
                      <svg xmlns="http://www.w3.org/2000/svg" className="size-4 text-white" viewBox="0 0 24 24" fill="currentColor">
                        <polygon points="5 3 19 12 5 21 5 3" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold leading-130 text-white">{lesson.name}</h3>
                    </div>
                  </div>
                  <span
                    onClick={() => {
                      setKeywordSheetOpen(false);
                      const qs = collectionId ? `?collection=${collectionId}` : "";
                      navigate(`/testing/lesson/${lesson.id}${qs}`);
                    }}
                    className="flex-center size-8 cursor-pointer rounded-full bg-orange-100"
                  >
                    <IconExternalLink className="size-4 text-dark" />
                  </span>
                </div>
              </div>

              <div className="flex flex-col gap-3 mb-6">
                <div className="flex items-start gap-2 p-3 rounded-xl bg-orange-100/[0.08] border border-orange-100/[0.14]">
                  <Info
                    className="size-4 text-orange-100 shrink-0 mt-px"
                    strokeWidth={2.2}
                  />
                  <p className="text-xs font-normal text-gray-1 leading-[18px]">
                    {t("enter_video_keyword_hint") ||
                      "Keyingi bosqichga o'tish uchun videoda keltirilgan kalit so'zni kiriting"}
                  </p>
                </div>
                <div className="w-full relative">
                  <Input
                    type="text"
                    ref={inputRef}
                    placeholder={t("enter_keyword_placeholder")}
                    className="border-white/08 pr-[calc(70px+1rem)]"
                    value={inputValue}
                    autoComplete="off"
                    onChange={(e) => setInputValue(e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={handlePaste}
                    className="py-1.5 z-10 px-1 w-[70px] text-xs font-medium leading-120 rounded-sm text-gray-1 bg-white/08 absolute top-1/2 -translate-y-1/2 right-2"
                  >
                    {t("paste")}
                  </button>
                </div>
              </div>
              <BaseButton
                type="submit"
                text={t("confirm")}
                className="w-full"
                isLoading={isVerifying}
                disabled={!inputValue.trim()}
              />
            </form>
          </Sheet.Content>
        </Sheet.Container>
        <Sheet.Backdrop className="!bg-[#0E0E14]/80" onTap={() => setKeywordSheetOpen(false)} />
      </Sheet>
    </>
  );
};

// ─── Video Player Modal ───

export const VideoPlayerModal = ({ videoUrl, onClose }: { videoUrl: string; onClose: () => void }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout>>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isPlaying, setIsPlaying] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [showControls, setShowControls] = useState(true);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) { v.play(); setIsPlaying(true); }
    else { v.pause(); setIsPlaying(false); }
  };

  const skip = (sec: number) => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = Math.min(Math.max(v.currentTime + sec, 0), v.duration || 0);
  };

  const fmt = (s: number) => {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = Math.floor(s % 60);
    return `${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
  };

  // Auto-hide controls
  useEffect(() => {
    if (showControls && isPlaying) {
      hideTimerRef.current = setTimeout(() => setShowControls(false), 3000);
      return () => { if (hideTimerRef.current) clearTimeout(hideTimerRef.current); };
    }
  }, [showControls, isPlaying]);

  const handleTap = () => {
    if (showControls) {
      togglePlay();
    } else {
      setShowControls(true);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black" onClick={handleTap}>
      {/* Video — fullscreen, centered */}
      {!isLoaded && (
        <div className="absolute inset-0 flex items-center justify-center z-10">
          <div className="w-10 h-10 border-3 border-white/20 border-t-white rounded-full animate-spin" />
        </div>
      )}
      <video
        ref={videoRef}
        src={videoUrl}
        autoPlay
        playsInline
        className={`absolute inset-0 w-full h-full object-contain pointer-events-none transition-opacity duration-300 ${isLoaded ? "opacity-100" : "opacity-0"}`}
        onLoadedData={() => setIsLoaded(true)}
        onTimeUpdate={() => setCurrentTime(videoRef.current?.currentTime || 0)}
        onLoadedMetadata={() => setDuration(videoRef.current?.duration || 0)}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={onClose}
      />

      {/* Controls overlay */}
      <div className={`absolute inset-0 z-20 transition-opacity duration-300 pointer-events-none ${showControls ? "opacity-100" : "opacity-0"}`}>
        {/* × close — yuqori o'ng */}
        <button
          onClick={(e) => { e.stopPropagation(); onClose(); }}
          className="absolute right-4 top-4 pt-safe-top text-white/70 active:text-white pointer-events-auto z-30"
        >
          <IconClose className="size-6" />
        </button>

        {/* Center controls — ⏪ ⏸ ⏩ */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center justify-center gap-8 pointer-events-auto" onClick={(e) => e.stopPropagation()}>
          <button onClick={() => skip(-10)} className="w-11 h-11 rounded-full bg-white/20 flex-center active:bg-white/30">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="size-5">
              <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" /><path d="M3 3v5h5" />
            </svg>
          </button>
          <button onClick={togglePlay} className="w-14 h-14 rounded-full bg-white/25 flex-center active:bg-white/35">
            {isPlaying ? (
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="white" className="size-7"><rect x="6" y="4" width="4" height="16" rx="1"/><rect x="14" y="4" width="4" height="16" rx="1"/></svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="white" className="size-7 ml-1"><polygon points="5 3 19 12 5 21 5 3"/></svg>
            )}
          </button>
          <button onClick={() => skip(10)} className="w-11 h-11 rounded-full bg-white/20 flex-center active:bg-white/30">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="size-5">
              <path d="M21 12a9 9 0 1 1-9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" /><path d="M21 3v5h-5" />
            </svg>
          </button>
        </div>

        {/* Bottom — seek bar + time */}
        <div className="absolute bottom-0 left-0 right-0 px-4 pb-safe-bottom pb-4 pt-6 bg-gradient-to-t from-black/60 to-transparent pointer-events-auto" onClick={(e) => e.stopPropagation()}>
          <input
            type="range" min={0} max={duration || 0} step={0.1} value={currentTime}
            onChange={(e) => { if (videoRef.current) videoRef.current.currentTime = Number(e.target.value); }}
            className="w-full h-1 mb-2 appearance-none rounded-full [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white"
            style={{ background: `linear-gradient(to right, white ${duration ? (currentTime / duration) * 100 : 0}%, rgba(255,255,255,0.2) ${duration ? (currentTime / duration) * 100 : 0}%)` }}
          />
          <span className="text-white/60 text-xs">{fmt(currentTime)} / {fmt(duration)}</span>
        </div>
      </div>
    </div>
  );
};

// ─── Test Start Button (opens DonDialog modal) ───

interface TestStartButtonDirectProps {
  testId: string;
  lessonId: string;
  collectionId: string | null;
  navigate: ReturnType<typeof useNavigate>;
  onWatchVideo: () => void;
  maxDon: number;
  totalQuestions: number;
}

const TestStartButtonDirect = ({ testId, lessonId, collectionId, navigate, onWatchVideo, maxDon, totalQuestions }: TestStartButtonDirectProps) => {
  const { t } = useTranslation();
  const { isOpen, onOpenChange, onClose } = useDialog();
  const { mutateAsync: startTest, isPending } = useStartTestMutation();
  const queryClient = useQueryClient();

  const handleStartTest = async () => {
    await startTest(testId);
    onClose();
    queryClient.invalidateQueries({ queryKey: [TestingKeys.TestDetail, testId] });
    navigate(`/testing/${lessonId}/test/${testId}${collectionId ? `?collection=${collectionId}` : ""}`);
  };

  return (
    <DonDialog
      onOpenChange={onOpenChange}
      isOpen={isOpen}
      title={t("start_test")}
      trigger={
        <TaskBaseButton className="bg-yellow-linear text-dark flex items-center text-xs">
          {t("test_start")}
          <ChevronRight className="size-4 shrink-0" />
        </TaskBaseButton>
      }
      description={
        <p className="text-white text-center leading-130 text-sm">
          <Trans
            i18nKey="watch_video_before_test"
            components={{ don: <span className="text-orange-100 uppercase" /> }}
          />
        </p>
      }
      footer={
        <div className="flex flex-col gap-2 w-full">
          <BaseButton
            text={t("watch_video")}
            className="bg-white/08"
            onClick={() => { onClose(); onWatchVideo(); }}
          />
          <BaseButton
            variant="premium"
            suffix={<IconChevron className="stroke-[1.5]" />}
            className="text-black"
            text={t("start_test")}
            onClick={handleStartTest}
            isLoading={isPending}
          />
        </div>
      }
    >
      <div className="w-full rounded-xl overflow-hidden bg-soft p-3">
        <div className="flex items-center justify-between mb-1">
          <div className="bg-yellow-linear bg-clip-text text-transparent text-sm leading-130 font-semibold flex items-center">
            {formatNumber(Number(maxDon) || 0)} DON
          </div>
        </div>
        <p className="text-gray-1 text-xs">
          {t("pass_rule", { count: Math.ceil(totalQuestions * 0.7) })}
        </p>
      </div>
    </DonDialog>
  );
};

export default LessonListPage;
